import styled from 'styled-components';
import { darken, lighten } from 'polished';

import { OrderSide } from '#/api/orders';

import { c080820, c185959, c00EE91, cFF005C, c14142E, c82044C } from '#/colors';

import toast from '#/components/common/toast';
import Title from '#/components/common/toast/Title';

import {
  useOrderFormActions,
  useOrderFormView,
} from '#/features/perpetuals/order-builder/form-context';
import { useOrderSubmit } from '#/features/perpetuals/order-builder/submit-context';
import { logException } from '#/features/logging/logging';

export default function SubmitOrder() {
  const formView = useOrderFormView();
  const formActions = useOrderFormActions();
  const { submit } = useOrderSubmit();
  const { isLoading } = useOrderSubmit();

  const handleSubmit = (side: OrderSide) => {
    if (!formView.isValid) return;

    submit(side, (resp) => {
      if (resp.ok) {
        formActions.reset();
      } else {
        const message = 'Failed to submit order';
        logException(new Error(message, { cause: resp.error }));
        toast.error(
          <>
            <Title>{message}</Title>
            {`[${resp.status}] ${resp.message}`}
          </>,
        );
      }
    });
  };

  const handleBuy = () => handleSubmit('BUY');

  const handleSell = () => handleSubmit('SELL');

  const isDisabled = isLoading || !formView.isValid;

  return (
    <div css="display: grid; grid-template-columns: 1fr 1fr; grid-gap: 12px;">
      <StyledSubmit side="BUY" disabled={isDisabled} onClick={handleBuy}>
        Buy
      </StyledSubmit>
      <StyledSubmit side="SELL" disabled={isDisabled} onClick={handleSell}>
        Sell
      </StyledSubmit>
    </div>
  );
}

interface SubmitProps {
  readonly side: OrderSide;
}

export const StyledSubmit = styled.button.attrs({
  type: 'submit',
})<SubmitProps>`
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 700;
  height: 32px;
  text-transform: capitalize;
  color: ${c080820};
  background-color: ${({ side }) => (side === 'BUY' ? c00EE91 : cFF005C)};

  &:hover {
    background-color: ${({ side }) =>
      lighten(0.05, side === 'BUY' ? c00EE91 : cFF005C)};
  }

  &:active {
    background-color: ${({ side }) =>
      darken(0.05, side === 'BUY' ? c00EE91 : cFF005C)};
  }

  &[disabled] {
    color: ${({ side }) => (side === 'BUY' ? c185959 : c82044C)};
    background-color: ${c14142E};
  }
`;
